import * as API from "../api"
import { getSourceDataForGroupKeyInfo } from "../common/ag-grid-grouping-utils"
import { sourceDataUpdated, sourceDataDeleted, actionError, setSaveStatus } from "../dashboard-data/actions"
import { setNotificationMessage } from "../websockets/actions"
import { PDF_CREATING } from "../websockets/types"
import { timekeepingDataIsEditable } from "../common/ag-grid-utils"
import { addClientDetailsToRequest } from "./form-view-actions"
import { READ_ONLY_WEEKLY_VIEW_RESOURCES } from "../common/constants"

const getBulkActionParams = (sourceData, filters, initialActionParams) => {
    const actionParams = Object.assign({}, initialActionParams)
    // an ugly way to allow button settings to reference filters on the dashboard (ex: projectId)
    for (const key in initialActionParams) {
        if (filters[key] != null) {
            actionParams[key] = filters[key]
        }
    }

    actionParams.resourceToId = {}
    // convert dictionary of resources to rows to dictionary of resources to ids
    for (const resource in sourceData) {
        actionParams.resourceToId[resource] = {
            gridIds: sourceData[resource].map(obj => obj.gridId),
            // rows that have not been synced to the server (ex: an empty row) do not have ids yet,
            // filter them out, we don't want to send undefined ids to the server
            object_ids: sourceData[resource].map(obj => obj.id).filter(id => id),
        }
    }
    return actionParams
}

const getSourceDataWithUpdatedValues = (sourceData, field, value) => {
    const updatedSourceData = {}
    for (const resource in sourceData) {
        updatedSourceData[resource] = sourceData[resource].map(row => {
            return { ...row, [field]: value }
        })
    }
    return updatedSourceData
}

export const bulkWorkflowActionWithLocation = (sourceData, filters, actionParams) => async dispatch => {
    const promises = []
    const apiActionParams = getBulkActionParams(sourceData, filters, actionParams)
    dispatch(setSaveStatus("in-progress"))

    for (const resourceName in apiActionParams.resourceToId) {
        promises.push(
            (async () => {
                const additionalData = await addClientDetailsToRequest({})
                try {
                    const result = await API.workflowActionById(
                        resourceName,
                        apiActionParams.resourceToId[resourceName].object_ids,
                        apiActionParams.action,
                        apiActionParams.queryParams,
                        additionalData
                    )
                    dispatch(
                        setNotificationMessage(PDF_CREATING, "info", {
                            ignoreIds: {
                                companyFormStores: apiActionParams.resourceToId[resourceName].object_ids,
                            },
                        })
                    )
                    return {
                        resourceName: resourceName,
                        data: result,
                    }
                } catch (result) {
                    throw {
                        resource: resourceName,
                        response: result.response.data,
                    }
                }
            })()
        )
    }

    try {
        const response = await Promise.all(promises)
        const updatedSourceData = {}
        // we need to append the correct gridIds to the rows in the server response
        response.forEach(resourceResponse => {
            updatedSourceData[resourceResponse.resourceName] = resourceResponse.data.map(responseRow => {
                const gridRow = sourceData[resourceResponse.resourceName].find(gridRow => gridRow.id === responseRow.id)
                return { ...responseRow, gridId: gridRow.gridId }
            })
        })
        dispatch(setSaveStatus("saved"))
        return dispatch(sourceDataUpdated(updatedSourceData))
    } catch (reason) {
        dispatch(setSaveStatus("failed"))
        errorResponseHandler(dispatch, sourceData, reason, "These rows cannot be updated.", actionParams.showErrorModal)
    }
}

export const bulkUpdateSourceDataField = (sourceData, filters, actionParams) => dispatch => {
    const promises = []
    const apiActionParams = getBulkActionParams(sourceData, filters, actionParams)
    dispatch(setSaveStatus("in-progress"))
    for (const resourceName in apiActionParams.resourceToId) {
        promises.push(
            new Promise((resolve, reject) => {
                API.updateItemsValueById(resourceName, apiActionParams.resourceToId[resourceName].object_ids, apiActionParams.field, apiActionParams.value)
                    .then(_ => resolve())
                    .catch(result =>
                        reject({
                            resource: resourceName,
                            response: result.response.data,
                        })
                    )
            })
        )
    }

    return Promise.all(promises).then(
        () => {
            const updatedSourceData = getSourceDataWithUpdatedValues(sourceData, actionParams.field, actionParams.value)
            dispatch(setSaveStatus("saved"))
            return dispatch(sourceDataUpdated(updatedSourceData))
        },
        reason => {
            dispatch(setSaveStatus("failed"))
            errorResponseHandler(dispatch, sourceData, reason, "These rows cannot be updated.", actionParams.showErrorModal)
        }
    )
}

export const bulkUpdateSourceDataFieldByGroupKeyInfo = (groupKeyInfo, field, value, context, options = {}) => (dispatch, getState) => {
    // ``rowsWithWorkRuleErrors`` is supplied by the caller because the annotations live on the
    // selector-produced ``rowData`` handed to AG-Grid, not on ``state.sourceData.sourceData``.
    // ``getSourceDataForGroupKeyInfo`` (used below) reads from raw state, so it can't detect
    // them. The cell renderer already has the annotated rows in scope and forwards a Set of
    // ``id ?? gridId`` for the rows we should refuse to update.
    const { rowsWithWorkRuleErrors = new Set() } = options
    const isBlockedByWorkRules = row => rowsWithWorkRuleErrors.has(row.id) || rowsWithWorkRuleErrors.has(row.gridId)

    const sourceDataToUpdate = getSourceDataForGroupKeyInfo(getState().sourceData.sourceData, groupKeyInfo, context)

    const cannotUpdateComponents = {}
    const canUpdateComponents = {}
    let workRuleBlockedCount = 0
    const tkStatuses = Object.values(getState().entities.timekeepingStatuses.objects)

    Object.entries(sourceDataToUpdate)
        .filter(([componentType, _]) => !READ_ONLY_WEEKLY_VIEW_RESOURCES.includes(componentType))
        .forEach(([componentType, tkComponentList]) => {
            tkComponentList.forEach(component => {
                if (isBlockedByWorkRules(component)) {
                    workRuleBlockedCount += 1
                } else if (!timekeepingDataIsEditable({ data: component, context }).canEdit) {
                    // ``component.status`` may be a string or a ``{name, ...}`` object; normalize
                    // to the name so the key is always a stable string (object keys would coerce
                    // to "[object Object]" and collapse distinct statuses into one bucket).
                    const statusKey = component.status && typeof component.status === "object" ? component.status.name : component.status
                    if (!(statusKey in cannotUpdateComponents)) cannotUpdateComponents[statusKey] = 0
                    cannotUpdateComponents[statusKey] += 1
                } else {
                    if (!(componentType in canUpdateComponents)) canUpdateComponents[componentType] = []
                    canUpdateComponents[componentType].push(component)
                }
            })
        })

    const proceed = () => dispatch(bulkUpdateSourceDataField(canUpdateComponents, {}, { field: field, value: value }))

    const hasEligibleRows = Object.values(canUpdateComponents).some(rows => rows.length > 0)
    const hasPermissionBlocks = Object.keys(cannotUpdateComponents).length > 0
    if (hasPermissionBlocks || workRuleBlockedCount > 0) {
        const permissionTotal = Object.values(cannotUpdateComponents).reduce((agg, entry) => agg + entry, 0)
        let descriptionDetail = ""
        Object.keys(cannotUpdateComponents).forEach(status => {
            const statusInfo = tkStatuses.find(s => s.name === status)
            const label = statusInfo?.label ?? status ?? "Unknown"
            const items = cannotUpdateComponents[status] > 1 ? "items" : "item"
            descriptionDetail += `<b>${label} - ${cannotUpdateComponents[status]} ${items}</b><br>`
        })
        const summaryParts = []
        if (permissionTotal > 0) {
            summaryParts.push(`You do not have permission to update ${permissionTotal} of the selected items:<br>` + descriptionDetail)
        }
        if (workRuleBlockedCount > 0) {
            const items = workRuleBlockedCount > 1 ? "items have" : "item has"
            summaryParts.push(`${workRuleBlockedCount} ${items} unresolved work rule errors and will not be updated.`)
        }
        if (!hasEligibleRows) {
            // Nothing left to update — present a single-button informational modal rather than
            // an "Update Remaining Items" prompt that would dispatch a no-op.
            summaryParts.push("No items are eligible to be updated.")
            context.createModalAction({
                title: "Cannot Update Statuses",
                description: summaryParts.join("<br>"),
                action: () => context.createModalAction(null),
                close: () => context.createModalAction(null),
                confirmButtonText: "OK",
                backgroundCloseEnabled: true,
            })
            return
        }
        context.createModalAction({
            title: "Issue While Updating Statuses",
            description: summaryParts.join("<br>"),
            action: proceed,
            buttonClass: "blueContinueButton",
            confirmButtonText: "Update Remaining Items",
            cancelButtonText: "Cancel All Updates",
            backgroundCloseEnabled: true,
            close: () => context.createModalAction(null),
        })
    } else return proceed()
}

export const deleteSourceData = (sourceData, filters, actionParams) => dispatch => {
    const promises = []
    const apiActionParams = getBulkActionParams(sourceData, filters, actionParams)
    dispatch(setSaveStatus("in-progress"))

    for (const resource in apiActionParams.resourceToId) {
        const args = [resource, apiActionParams.resourceToId[resource].object_ids, apiActionParams["current_project_id"]]
        promises.push(
            new Promise((resolve, reject) => {
                API.deleteItemsById(...args)
                    .then(() => resolve())
                    .catch(result =>
                        reject({
                            resource: resource,
                            response: result.response.data,
                        })
                    )
            })
        )
    }
    return Promise.all(promises).then(
        () => {
            dispatch(setSaveStatus("saved"))
            return dispatch(sourceDataDeleted(sourceData))
        },
        reason => {
            dispatch(setSaveStatus("failed"))
            errorResponseHandler(dispatch, sourceData, reason, "These objects cannot be deleted", actionParams.showErrorModal)
        }
    )
}

const errorResponseHandler = (dispatch, sourceData, reason, message, showErrorModal) => {
    if (showErrorModal) {
        return dispatch(actionError(true, "You do not have permission to perform this action."))
    }
    const { response, resource } = reason
    const key = Object.keys(response)
    if (response && key.length && response[key[0]] && response[key[0]].length > 0) {
        if (key[0] === "detail") {
            return dispatch(actionError(true, response[key[0]]))
        }
        setSourceDataErrors(dispatch, sourceData, resource, response, key[0], message)
    } else {
        dispatch(actionError(true, null))
    }
}

const setSourceDataErrors = (dispatch, sourceData, resource, response, key, message) => {
    const sourceDataThatShouldHaveErrors = {
        [resource]: sourceData[resource].filter(row => response[key].findIndex(id => id === row.id) !== -1),
    }
    const sourceDataWithErrors = getSourceDataWithUpdatedValues(sourceDataThatShouldHaveErrors, "errors", {
        row: message,
    })
    dispatch(sourceDataUpdated(sourceDataWithErrors))
}
